import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { usePartial } from 'shared/hooks/utils';
import { show_toast } from 'console/dialogs/toast';
import { useDeviceState } from 'console/shared/contexts/device/state';
import { ModeIcon } from '../components/mode-icon';
import { useLongPressAction } from 'shared/hooks/ui/long-press';
import { useHeatingMode } from '../provider';
import { useHeatingModeControl } from '../provider/z3k';
import * as S from '../styles';
import { Z3kRoutes, Z3kSecondRoutes } from 'z3k-settings/routes/type';
import { GoToSettings } from 'console/shared/components/dialogs/settings';
import { formatTemp } from 'utils';
import { useAction } from 'shared/hooks/redux/hooks';
export const HeatingModeCard = memo(function HeatingModeCard(props) {
    const { show_settings, heating_circuit_id, small } = props;
    const { id, active, disabled, color, onSelectMode, loading, temperature, schedule_number, name, not_configured, fail, fail_message, } = useHeatingMode();
    const { setSelectedModeId } = useHeatingModeControl();
    const { online } = useDeviceState();
    const onShowToast = useAction(show_toast);
    const onActiveClick = useCallback(() => onShowToast('Режим уже применен'), [onShowToast]);
    const onNotConfiguredClick = useCallback(() => onShowToast('Для режима не заданы настройки'), [onShowToast]);
    const onDisabledClick = useCallback(() => onShowToast('Невозможно применить режим'), [onShowToast]);
    const onOfflineClick = useCallback(() => onShowToast('Невозможно применить режим, устройство не на связи'), [onShowToast]);
    const onModeClick = usePartial(useAction(onSelectMode), heating_circuit_id);
    const { active_on_panel, active_in_widget } = useMemo(() => {
        const active_on_panel = active && disabled;
        const active_in_widget = heating_circuit_id && active;
        return { active_on_panel, active_in_widget };
    }, [active, disabled, heating_circuit_id]);
    const onIncorrectClick = useCallback(() => {
        if (!online) {
            return onOfflineClick;
        }
        if (loading) {
            return onDisabledClick;
        }
        if (not_configured) {
            return onNotConfiguredClick;
        }
        if (active_on_panel || active_in_widget) {
            return onActiveClick;
        }
        return undefined;
    }, [loading, active_on_panel, active_in_widget, online, not_configured]);
    const onLongPressEnd = useCallback(() => {
        const incorrect_action = onIncorrectClick();
        if (incorrect_action) {
            return incorrect_action();
        }
        navigator.vibrate?.(200);
        setSelectedModeId(id);
        return onModeClick();
    }, [
        id,
        onModeClick,
        not_configured,
        setSelectedModeId,
        active_on_panel,
        active_in_widget,
        online,
    ]);
    const can_turned_on_again = useMemo(() => active && !disabled && !loading && !active_in_widget, [active, disabled, active_in_widget, loading]);
    const [show_fail, setShowFail] = useState(false);
    const long_press_handlers = useLongPressAction({
        onLongPressEnd,
        color,
        active,
        loading,
        can_turned_on_again,
        can_turned_off: false,
        fail: show_fail,
    });
    useEffect(() => {
        if (fail) {
            onShowToast(fail_message || 'Произошла ошибка');
            setShowFail(true);
            setTimeout(() => setShowFail(false), 2000);
            setSelectedModeId(null);
        }
    }, [fail]);
    return (_jsx(S.HeatingModeCard, { children: _jsxs(S.HeatingModeButton, { small: small, color: color, onTouchStart: onIncorrectClick(), ...long_press_handlers, className: "heating-mode-button", tabIndex: -2, children: [_jsxs(Box, { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", alignItems: "center", children: [_jsx(ModeIcon, { id: id, name: name, large: true }), typeof schedule_number !== 'number'
                            ? !small &&
                                temperature !== null && (_jsx(Typography, { fontSize: "0.6em", textAlign: "right", whiteSpace: "nowrap", children: formatTemp(temperature) }))
                            : null, show_settings && (_jsx(Box, { display: "flex", color: active ? 'error.contrastText' : 'action.active', gridColumn: "2", justifySelf: "flex-end", children: _jsx(GoToSettings, { groupKey: Z3kRoutes.heating_modes, itemKey: Z3kSecondRoutes.heating_modes, objectId: id }) }))] }), _jsxs(Box, { display: "grid", gridTemplateColumns: temperature && small ? '3fr 1fr' : '1fr', alignItems: "center", children: [_jsx(Typography, { fontSize: "0.6em", textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", children: name }), temperature && small ? (_jsx(Typography, { fontSize: "0.6em", textAlign: "right", whiteSpace: "nowrap", children: formatTemp(temperature) })) : null] })] }) }, id));
});
